import React, {
  createContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  useContext,
} from "react";
import { useSelector } from "react-redux";
import { selectGame } from "../store/slices/global";
import { MtgCardType, MtgCardTypeComplex } from "../type";
import { LoadingOverlay } from "../components/common/LoadingOverley/LoadingOverley";

const CardsContext = createContext<Record<number, MtgCardTypeComplex>>({});
const MinimalCardsContext = createContext<MtgCardType[]>([]);

// NEW: Search Contexts
const SearchResultsContext = createContext<any[]>([]);
const SearchFunctionContext = createContext<(query: string) => void>(() => {});
const SearchReadyContext = createContext<boolean>(false);
const ClearSearchResultsContext = createContext<() => void>(() => {});

const normalizeEditionAndNumber = (edition: string, number: string): string =>
  `${edition.toLowerCase()} ${number.trim()}`;

const sanitize = (str: string): string =>
  str
    .replace(/[^a-zA-Z0-9 ]/g, "")
    .replace(/\s+/g, " ")
    .toLowerCase()
    .trim();

export const CardsProvider = ({ children }: { children: React.ReactNode }) => {
  const game = useSelector(selectGame);
  const [cards, setCards] = useState<Record<number, MtgCardTypeComplex>>({});
  const [cardsMinimal, setCardsMinimal] = useState<MtgCardType[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Search state
  const [searchResults, setSearchResults] = useState<MtgCardType[]>([]);
  const [isSearchReady, setIsSearchReady] = useState(false);
  const searchWorkerRef = useRef<Worker | null>(null);

  const clearSearchResults = () => setSearchResults([]);

  const filePath = `all_data_cards_${game?.card_trader_game || "mtg"}.json`;

  useEffect(() => {
    if (!filePath) return;

    setIsLoading(true);
    fetch(`/${filePath}`)
      .then((res) => res.json())
      .then((data: MtgCardTypeComplex[]) => {
        // Full and minimal card state
        setCards(Object.fromEntries(data.map((card) => [card.id, card])));
        const minimal = data.map((card) => ({
          id: card.id,
          name: card.name,
          name_it: card?.name_it,
          name_de: card?.name_de,
          name_es: card?.name_es,
          name_fr: card?.name_fr,
          name_pt: card?.name_pt,
          fixed_properties: {
            collector_number: card?.fixed_properties?.collector_number
              ? String(card.fixed_properties.collector_number).padStart(3, "0")
              : "",
          },
          card_market_ids: card?.card_market_ids,
          image_url: card.image_url,
          editionCode: card.editionCode,
          editionName: card.editionName,
        }));
        setCardsMinimal(minimal);

        // 💡 Process & send to worker
        const processedData = minimal.map((card) => {
          const editionNumber =
            card.editionCode && card.fixed_properties?.collector_number
              ? normalizeEditionAndNumber(
                  card.editionCode,
                  card.fixed_properties.collector_number
                )
              : "";

          return {
            ...card,
            nameSanitized: sanitize(card.name),
            name_itSanitized: sanitize(card.name_it || ""),
            editionNumberSanitized: sanitize(editionNumber),
            /* name_frSanitized: sanitize(card.name_fr || ""),
            name_deSanitized: sanitize(card.name_de || ""),
            name_esSanitized: sanitize(card.name_es || ""),
            name_ptSanitized: sanitize(card.name_pt || ""), */
            // nameWithEditionSanitized: sanitize(`${card.name} ${editionNumber}`),
          };
        });

        // Create and initialize the worker
        const worker = new Worker(
          new URL("../workers/searchWorker.worker.js", import.meta.url),
          { type: "module" }
        );
        searchWorkerRef.current = worker;

        const searchConfig = {
          fields: [
            { name: "nameSanitized", weight: 0.8, threshold: 0 },
            { name: "editionNumberSanitized", weight: 0.7, threshold: 0.1 },
            { name: "nameWithEditionSanitized", weight: 0.1, threshold: 0 },
            { name: "name_frSanitized", weight: 0.1, threshold: 0 },
            { name: "name_deSanitized", weight: 0.1, threshold: 0 },
            { name: "name_esSanitized", weight: 0.1, threshold: 0 },
            { name: "name_itSanitized", weight: 0.1, threshold: 0 },
          ],
          fuseOptions: {
            distance: 0,
            ignoreLocation: true,
            includeScore: true,
          },
        };

        worker.postMessage({
          type: "init",
          data: { cards: processedData, config: searchConfig },
        });

        worker.onmessage = (e: MessageEvent) => {
          const { type, results } = e.data;
          if (type === "results") {
            setSearchResults(results);
          }
          if (type === "init-complete") {
            setIsSearchReady(true);
          }
        };
      })
      .finally(() => setIsLoading(false));

    return () => {
      // Cleanup
      searchWorkerRef.current?.terminate();
      searchWorkerRef.current = null;
      setIsSearchReady(false);
    };
  }, [filePath]);

  // Expose a global search function
  const search = useCallback(
    (query: string) => {
      if (!query || !searchWorkerRef.current || !isSearchReady) return;
      const cleaned = sanitize(query);
      if (cleaned.length < 3) {
        setSearchResults([]);
        return;
      }

      searchWorkerRef.current.postMessage({
        type: "search",
        data: { query: cleaned },
      });
    },
    [isSearchReady]
  );

  return (
    <CardsContext.Provider value={cards}>
      <MinimalCardsContext.Provider value={cardsMinimal}>
        <SearchReadyContext.Provider value={isSearchReady}>
          <SearchResultsContext.Provider value={searchResults}>
            <SearchFunctionContext.Provider value={search}>
              <ClearSearchResultsContext.Provider value={clearSearchResults}>
                {isLoading && <LoadingOverlay />}
                {children}
              </ClearSearchResultsContext.Provider>
            </SearchFunctionContext.Provider>
          </SearchResultsContext.Provider>
        </SearchReadyContext.Provider>
      </MinimalCardsContext.Provider>
    </CardsContext.Provider>
  );
};

export const useCards = () => useContext(CardsContext);
export const useMinimalCards = () => useContext(MinimalCardsContext);
export const useSearchResults = () => useContext(SearchResultsContext);
export const useSearchFunction = () => useContext(SearchFunctionContext);
export const useSearchReady = () => useContext(SearchReadyContext);
export const useClearSearchResults = () =>
  useContext(ClearSearchResultsContext);
